import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

type AdminNotification = {
  type: "user" | "course" | "submission" | "chat";
  title: string;
  description: string;
  senderId?: number;
};

type ChatUser = {
  id: number;
  username: string;
};

type ChatUnreadCounts = {
  [senderId: string]: number;
};

function AdminNotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [readIndexes, setReadIndexes] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [chatUsers, setChatUsers] = useState<ChatUser[]>([]);
  const [chatUnreadCounts, setChatUnreadCounts] = useState<ChatUnreadCounts>({});

  const getLoggedInAdminId = () => {
  const storedUser = sessionStorage.getItem("loggedInUser");

  if (!storedUser) {
    return null;
  }

  const user = JSON.parse(storedUser);
  return user.id || null;
};

const fetchChatUsers = () => {
  fetch("/chat/users")
    .then((response) => response.json())
    .then((data) => setChatUsers(data))
    .catch((error) => {
      console.error("Chat users fetch error:", error);
    });
};

const fetchChatUnreadCounts = () => {
  const adminId = getLoggedInAdminId();

  if (!adminId) {
    return;
  }

  fetch(`/chat/unread-counts?user_id=${adminId}`)
    .then((response) => response.json())
    .then((data) => setChatUnreadCounts(data))
    .catch((error) => {
      console.error("Chat unread counts fetch error:", error);
    });
};

  useEffect(() => {
    fetch("/admin/notifications")
      .then((response) => response.json())
      .then((data) => setNotifications(data))
      .catch((error) => {
        console.error("Admin notifications fetch error:", error);
      });

    const savedRead = localStorage.getItem("adminReadNotificationIndexes");
    if (savedRead) {
      setReadIndexes(JSON.parse(savedRead));
    }

    fetchChatUsers();
fetchChatUnreadCounts();

const intervalId = window.setInterval(() => {
  fetchChatUnreadCounts();
}, 3000);

return () => {
  window.clearInterval(intervalId);
};
  }, []);

  const filteredNotifications = notifications.filter((notification) => {
    const keyword = searchTerm.trim().toLowerCase();

    if (!keyword) return true;

    return (
      notification.title.toLowerCase().includes(keyword) ||
      notification.description.toLowerCase().includes(keyword) ||
      notification.type.toLowerCase().includes(keyword)
    );
  });

  const chatNotifications: AdminNotification[] = Object.entries(chatUnreadCounts)
  .filter(([, count]) => count > 0)
  .map(([senderId, count]) => {
    const sender = chatUsers.find((user) => user.id === Number(senderId));

    return {
      type: "chat",
      title: "新しいチャットメッセージ",
      description: `${sender?.username || "ユーザー"} さんから ${count} 件の新しいメッセージがあります。`,
      senderId: Number(senderId),
    };
  });

const allFilteredNotifications = [
  ...chatNotifications,
  ...filteredNotifications,
];

const chatUnreadCount = Object.values(chatUnreadCounts).reduce(
  (total, count) => total + count,
  0
);

 const unreadCount =
  notifications.filter((_, index) => !readIndexes.includes(index)).length +
  chatUnreadCount;

  const markAsRead = (index: number) => {
    const updated = Array.from(new Set([...readIndexes, index]));
    setReadIndexes(updated);
    localStorage.setItem("adminReadNotificationIndexes", JSON.stringify(updated));
  };

  const markAllAsRead = () => {
    const allIndexes = notifications.map((_, index) => index);
    setReadIndexes(allIndexes);
    localStorage.setItem("adminReadNotificationIndexes", JSON.stringify(allIndexes));
  };

  const getIcon = (type: AdminNotification["type"]) => {
    if (type === "user") return "👥";
    if (type === "course") return "📘";
    return "📝";
  };

  const getActionLabel = (type: AdminNotification["type"]) => {
    if (type === "user") return "ユーザーを確認する";
    if (type === "course") return "コースを確認する";
    return "テスト提出を確認する";
  };

  const handleOpenTargetPage = (type: AdminNotification["type"]) => {
    if (type === "user") {
      navigate("/users");
      return;
    }

    if (type === "course") {
      navigate("/courses");
      return;
    }

    navigate("/tests");
  };

  return (
    <div style={pageStyle}>
      <main style={mainStyle}>
        <header style={topHeaderStyle}>
          <div>
            <h1 style={pageTitleStyle}>通知</h1>
            <p style={pageSubtitleStyle}>管理者向けのお知らせを確認できます</p>
          </div>

          <div style={topRightStyle}>
            <div style={searchBoxStyle}>
              <span style={{ color: "#64748b" }}>🔍</span>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="通知内容・ユーザー・コース・テストで検索"
                style={searchInputStyle}
              />
            </div>

            <div style={notificationBellStyle}>
              🔔
              {unreadCount > 0 && (
                <span style={notificationBellBadgeStyle}>{unreadCount}</span>
              )}
            </div>
          </div>
        </header>

        <button
          onClick={() => navigate("/admin-dashboard")}
          style={backButtonStyle}
        >
          ← ダッシュボードへ戻る
        </button>

        <div style={notificationTopBarStyle}>
          <div style={unreadBadgeStyle}>{unreadCount} 件未読</div>

          <button onClick={markAllAsRead} style={markAllReadButtonStyle}>
            ✓ すべて既読にする
          </button>
        </div>

        <section style={notificationPageStyle}>
          {allFilteredNotifications.length === 0 && (
            <div style={emptySectionCardStyle}>現在、新しい通知はありません。</div>
          )}

          {notifications.length > 0 && allFilteredNotifications.length === 0 && (
            <div style={emptySectionCardStyle}>検索結果が見つかりません。</div>
          )}

          {allFilteredNotifications.map((notification) => {

            if (notification.type === "chat") {
  return (
    <div
      key={`chat-${notification.senderId}`}
      style={notificationLargeCardStyle}
    >
      <div style={notificationIconBoxStyle}>💬</div>

      <div style={{ flex: 1 }}>
        <div style={notificationTitleStyle}>{notification.title}</div>
        <div style={notificationMessageStyle}>{notification.description}</div>
        <div style={notificationDateStyle}>チャット通知</div>
      </div>

      <button
        onClick={() => navigate("/chat")}
        style={notificationOpenButtonStyle}
      >
        チャットを開く
      </button>
    </div>
  );
}



            const originalIndex = notifications.findIndex(
              (item) =>
                item.type === notification.type &&
                item.title === notification.title &&
                item.description === notification.description
            );

            const isRead = readIndexes.includes(originalIndex);

            return (
              <div
                key={`${notification.type}-${originalIndex}`}
                style={isRead ? notificationReadCardStyle : notificationLargeCardStyle}
              >
                <div style={notificationIconBoxStyle}>
                  {getIcon(notification.type)}
                </div>

                <div style={{ flex: 1 }}>
                  <div style={notificationTitleStyle}>{notification.title}</div>
                  <div style={notificationMessageStyle}>
                    {notification.description}
                  </div>
                  <div style={notificationDateStyle}>自動通知</div>
                </div>

                <button
                  onClick={() => handleOpenTargetPage(notification.type)}
                  style={notificationOpenButtonStyle}
                >
                  {getActionLabel(notification.type)}
                </button>

                {!isRead && (
                  <button
                    onClick={() => markAsRead(originalIndex)}
                    style={markReadButtonStyle}
                  >
                    既読にする
                  </button>
                )}
              </div>
            );
          })}
        </section>
      </main>
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  minHeight: "100vh",
  backgroundColor: "#f8fafc",
  color: "#0f172a",
};

const mainStyle: React.CSSProperties = {
  width: "100%",
  padding: "38px 32px",
  boxSizing: "border-box",
};

const topHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "20px",
};

const topRightStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "18px",
};

const searchBoxStyle: React.CSSProperties = {
  width: "420px",
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "14px",
  padding: "12px 16px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

const searchInputStyle: React.CSSProperties = {
  width: "100%",
  border: "none",
  outline: "none",
  fontSize: "15px",
  color: "#0f172a",
  backgroundColor: "transparent",
};

const notificationBellStyle: React.CSSProperties = {
  position: "relative",
  fontSize: "22px",
  cursor: "default",
};

const notificationBellBadgeStyle: React.CSSProperties = {
  position: "absolute",
  top: "-10px",
  right: "-12px",
  width: "22px",
  height: "22px",
  borderRadius: "50%",
  backgroundColor: "#0f766e",
  color: "#ffffff",
  fontSize: "12px",
  fontWeight: 800,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const pageTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "34px",
  fontWeight: 900,
};

const pageSubtitleStyle: React.CSSProperties = {
  margin: "8px 0 0",
  color: "#64748b",
  fontSize: "16px",
};

const backButtonStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  color: "#0f172a",
  border: "1px solid #cbd5e1",
  borderRadius: "14px",
  padding: "12px 18px",
  fontWeight: 800,
  cursor: "pointer",
  marginBottom: "24px",
};

const notificationTopBarStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "24px",
};

const unreadBadgeStyle: React.CSSProperties = {
  display: "inline-block",
  backgroundColor: "#ccfbf1",
  color: "#0f766e",
  padding: "8px 16px",
  borderRadius: "999px",
  fontSize: "14px",
  fontWeight: 800,
};

const markAllReadButtonStyle: React.CSSProperties = {
  border: "none",
  backgroundColor: "#ffffff",
  color: "#0f172a",
  padding: "12px 18px",
  borderRadius: "14px",
  fontWeight: 800,
  cursor: "pointer",
  boxShadow: "0 4px 14px rgba(15, 23, 42, 0.06)",
};

const notificationPageStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "16px",
};

const notificationLargeCardStyle: React.CSSProperties = {
  backgroundColor: "#f0fdfa",
  border: "1px solid #99f6e4",
  borderRadius: "18px",
  padding: "22px",
  display: "flex",
  alignItems: "center",
  gap: "18px",
};

const notificationReadCardStyle: React.CSSProperties = {
  ...notificationLargeCardStyle,
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
};

const notificationIconBoxStyle: React.CSSProperties = {
  width: "46px",
  height: "46px",
  borderRadius: "14px",
  backgroundColor: "#ccfbf1",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "22px",
};

const notificationTitleStyle: React.CSSProperties = {
  fontSize: "18px",
  fontWeight: 900,
  color: "#0f172a",
  marginBottom: "8px",
};

const notificationMessageStyle: React.CSSProperties = {
  color: "#64748b",
  fontSize: "15px",
};

const notificationDateStyle: React.CSSProperties = {
  marginTop: "14px",
  color: "#64748b",
  fontSize: "13px",
};

const notificationOpenButtonStyle: React.CSSProperties = {
  border: "none",
  backgroundColor: "#0f766e",
  color: "#ffffff",
  padding: "10px 16px",
  borderRadius: "12px",
  fontWeight: 800,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const markReadButtonStyle: React.CSSProperties = {
  border: "none",
  backgroundColor: "#14b8a6",
  color: "#ffffff",
  padding: "10px 16px",
  borderRadius: "12px",
  fontWeight: 800,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const emptySectionCardStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "18px",
  padding: "40px",
  color: "#64748b",
  fontSize: "18px",
  fontWeight: 700,
};

export default AdminNotificationsPage;